import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { DisparoLead, DisparoLeadFilters } from "@/lib/disparos-types";
import { normalizePhoneDigits } from "@/lib/format-phone";
import {
  leadMatchesDisparoSearch,
  prepareLeadSearchTerm,
  sortLeadsBySearchRelevance,
} from "@/lib/leads-kanban-utils";

const PAGE_SIZE = 200;
/** Limite do RPC `search_leads_by_term` (máx. 50 no servidor). */
const SEARCH_LIMIT = 50;

type DisparoLeadRow = DisparoLead & { created_at?: string };

function postgrestQuotedLiteral(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/** Cursor composto (created_at DESC, id DESC) — evita pular leads com mesmo timestamp. */
function buildDescendingKeysetOrFilter(cursorCreatedAt: string, cursorId: string): string {
  const createdAt = postgrestQuotedLiteral(cursorCreatedAt);
  const id = postgrestQuotedLiteral(cursorId);
  return `created_at.lt.${createdAt},and(created_at.eq.${createdAt},id.lt.${id})`;
}

/**
 * Telefone utilizável para disparo/WhatsApp: extrai só dígitos (formatação arbitrária)
 * e exige comprimento mínimo para número discável (BR sem DDI ou internacional).
 */
function normalizePhone(phone: string | null | undefined): string | null {
  const digits = normalizePhoneDigits(phone);
  return digits.length >= 10 ? digits : null;
}

async function fetchTagsByLeadId(leadIds: string[]): Promise<Map<string, Set<string>>> {
  const tagsByLead = new Map<string, Set<string>>();
  if (leadIds.length === 0) return tagsByLead;

  const { data } = await supabase
    .from("lead_tag_assignments")
    .select("lead_id, tag_id")
    .in("lead_id", leadIds);

  for (const row of data || []) {
    const lid = String((row as { lead_id: string }).lead_id);
    if (!tagsByLead.has(lid)) tagsByLead.set(lid, new Set());
    tagsByLead.get(lid)!.add(String((row as { tag_id: string }).tag_id));
  }
  return tagsByLead;
}

function leadMatchesTags(leadId: string, tagIds: string[], tagsByLead: Map<string, Set<string>>): boolean {
  if (tagIds.length === 0) return true;
  const leadTags = tagsByLead.get(leadId);
  return tagIds.every((tid) => leadTags?.has(tid));
}

function toDisparoLead(row: DisparoLeadRow): DisparoLead {
  return {
    id: row.id,
    name: row.name,
    phone: row.phone,
    email: row.email,
    stage: row.stage,
    funnel_id: row.funnel_id,
  };
}

type StageMeta = { slug: string; name: string; order_index: number };

async function fetchFunnelNameMap(funnelIds: string[]): Promise<Map<string, string>> {
  if (funnelIds.length === 0) return new Map();
  const { data } = await supabase.from("sales_funnels").select("id, name").in("id", funnelIds);
  return new Map((data || []).map((f) => [String(f.id), String(f.name)]));
}

async function fetchStagesByFunnelId(funnelIds: string[]): Promise<Map<string, StageMeta[]>> {
  const byFunnel = new Map<string, StageMeta[]>();
  if (funnelIds.length === 0) return byFunnel;

  const { data } = await supabase
    .from("lead_stages")
    .select("funnel_id, slug, name, order_index")
    .in("funnel_id", funnelIds)
    .order("order_index");

  for (const row of data || []) {
    const funnelId = String((row as { funnel_id: string }).funnel_id);
    if (!byFunnel.has(funnelId)) byFunnel.set(funnelId, []);
    byFunnel.get(funnelId)!.push({
      slug: String((row as { slug: string }).slug),
      name: String((row as { name: string }).name),
      order_index: Number((row as { order_index: number }).order_index),
    });
  }
  return byFunnel;
}

function compareLeadNames(a: DisparoLead, b: DisparoLead): number {
  return (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" });
}

async function enrichDisparoLeads(
  leads: DisparoLead[],
  filters: DisparoLeadFilters,
): Promise<DisparoLead[]> {
  if (leads.length === 0) return leads;

  const needsFunnelNames = !filters.funnelId;
  const needsStageMeta = !filters.stageSlug;
  if (!needsFunnelNames && !needsStageMeta) {
    return [...leads].sort(compareLeadNames);
  }

  const funnelIds = filters.funnelId
    ? [filters.funnelId]
    : ([...new Set(leads.map((l) => l.funnel_id).filter(Boolean))] as string[]);

  const [funnelNames, stagesByFunnel] = await Promise.all([
    needsFunnelNames ? fetchFunnelNameMap(funnelIds) : Promise.resolve(new Map<string, string>()),
    needsStageMeta ? fetchStagesByFunnelId(funnelIds) : Promise.resolve(new Map<string, StageMeta[]>()),
  ]);

  const enriched = leads.map((lead) => {
    const stages = lead.funnel_id ? stagesByFunnel.get(lead.funnel_id) : undefined;
    const stageMeta = stages?.find((s) => s.slug === lead.stage);
    return {
      ...lead,
      funnel_name: needsFunnelNames && lead.funnel_id ? funnelNames.get(lead.funnel_id) ?? null : lead.funnel_name,
      stage_name: stageMeta?.name ?? lead.stage ?? null,
      stage_order_index: stageMeta?.order_index ?? null,
    };
  });

  if (filters.funnelId && !filters.stageSlug && filters.tagIds.length === 0) {
    return enriched;
  }

  return enriched.sort(compareLeadNames);
}

function applyDisparoPostFilters(
  rows: DisparoLeadRow[],
  filters: DisparoLeadFilters,
  tagsByLead: Map<string, Set<string>>,
): DisparoLead[] {
  const out: DisparoLead[] = [];
  for (const lead of rows) {
    if (!normalizePhone(lead.phone)) continue;
    if (filters.stageSlug && lead.stage !== filters.stageSlug) continue;
    if (!leadMatchesTags(lead.id, filters.tagIds, tagsByLead)) continue;
    out.push(toDisparoLead(lead));
  }
  return out;
}

async function fetchDisparoLeadsBySearch(
  workspaceId: string,
  filters: DisparoLeadFilters,
  searchRaw: string,
): Promise<DisparoLead[]> {
  const searchQuery = prepareLeadSearchTerm(searchRaw);
  if (!searchQuery) return [];

  const { data, error } = await supabase.rpc("search_leads_by_term", {
    p_workspace_id: workspaceId,
    p_term: searchQuery,
    p_limit: SEARCH_LIMIT,
    p_funnel_id: filters.funnelId || null,
    p_include_null_funnel: false,
  });
  if (error) throw error;

  const rows = sortLeadsBySearchRelevance((data || []) as DisparoLeadRow[], searchRaw);
  const tagsByLead =
    filters.tagIds.length > 0 ? await fetchTagsByLeadId(rows.map((l) => l.id)) : new Map();

  const filtered = applyDisparoPostFilters(rows, filters, tagsByLead);
  return filtered.filter((lead) => leadMatchesDisparoSearch(lead, searchRaw));
}

async function fetchDisparoLeadsBrowse(
  workspaceId: string,
  filters: DisparoLeadFilters,
): Promise<DisparoLead[]> {
  const all: DisparoLead[] = [];
  let cursorCreatedAt: string | null = null;
  let cursorId: string | null = null;
  let hasMore = true;

  while (hasMore) {
    let q = supabase
      .from("leads")
      .select("id, name, phone, email, stage, funnel_id, created_at")
      .eq("workspace_id", workspaceId)
      .not("phone", "is", null)
      .neq("phone", "")
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(PAGE_SIZE + 1);

    if (cursorCreatedAt && cursorId) {
      q = q.or(buildDescendingKeysetOrFilter(cursorCreatedAt, cursorId));
    }

    if (filters.funnelId) q = q.eq("funnel_id", filters.funnelId);
    if (filters.stageSlug) q = q.eq("stage", filters.stageSlug);

    const { data, error } = await q;
    if (error) throw error;

    const rows = (data || []) as DisparoLeadRow[];
    hasMore = rows.length > PAGE_SIZE;
    const page = rows.slice(0, PAGE_SIZE);

    const tagsByLead =
      filters.tagIds.length > 0 ? await fetchTagsByLeadId(page.map((l) => l.id)) : new Map();

    all.push(...applyDisparoPostFilters(page, filters, tagsByLead));

    if (hasMore && page.length > 0) {
      const last = page[page.length - 1];
      const nextCreatedAt = last.created_at ?? null;
      const nextId = last.id;
      if (nextCreatedAt === cursorCreatedAt && nextId === cursorId) {
        console.error("useDisparoLeads: cursor não avançou, interrompendo paginação");
        hasMore = false;
      } else {
        cursorCreatedAt = nextCreatedAt;
        cursorId = nextId;
      }
    } else {
      hasMore = false;
    }
  }

  return all;
}

export function useDisparoLeads(workspaceId: string | null, filters: DisparoLeadFilters) {
  const [leads, setLeads] = useState<DisparoLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedLeadById, setSelectedLeadById] = useState<Map<string, DisparoLead>>(new Map());
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const fetchGenerationRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const fetchLeads = useCallback(async () => {
    if (!workspaceId) return;
    const generation = ++fetchGenerationRef.current;
    setLoading(true);
    try {
      const searchRaw = debouncedSearch.trim();
      const raw = searchRaw
        ? await fetchDisparoLeadsBySearch(workspaceId, filters, searchRaw)
        : await fetchDisparoLeadsBrowse(workspaceId, filters);
      if (generation !== fetchGenerationRef.current) return;
      const results = await enrichDisparoLeads(raw, filters);
      if (generation !== fetchGenerationRef.current) return;
      setLeads(results);
    } catch (e) {
      if (generation !== fetchGenerationRef.current) return;
      console.error("useDisparoLeads fetch error:", e);
      setLeads([]);
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [workspaceId, filters, debouncedSearch]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const selectedLeads = useMemo(
    () => [...selectedLeadById.values()],
    [selectedLeadById],
  );

  const toggleLead = useCallback((leadId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(leadId);
      else next.delete(leadId);
      return next;
    });
    setSelectedLeadById((prev) => {
      const next = new Map(prev);
      if (checked) {
        const lead = leads.find((l) => l.id === leadId);
        if (lead) next.set(leadId, lead);
      } else {
        next.delete(leadId);
      }
      return next;
    });
  }, [leads]);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(leads.map((l) => l.id)));
    setSelectedLeadById((prev) => {
      const next = new Map(prev);
      for (const lead of leads) {
        next.set(lead.id, lead);
      }
      return next;
    });
  }, [leads]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedLeadById(new Map());
  }, []);

  return {
    leads,
    loading,
    selectedIds,
    selectedLeads,
    selectedCount: selectedIds.size,
    toggleLead,
    selectAll,
    deselectAll,
    refetch: fetchLeads,
  };
}

export function leadToRecipient(lead: DisparoLead) {
  const phone = normalizePhone(lead.phone);
  if (!phone) return null;
  return { lead_id: lead.id, phone, name: lead.name || undefined };
}
